// per docs/shape-uc-27-alert-triggers-and-state.md §2 §5
// per docs/caiet-uc-spec.md §UC27-01 §UC-ALRT-01 §UC-ALRT-03 §UC-ALRT-08
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import type { TableColumn, TableRowData } from '@egov-moldova/mud/loader';
import {
  MudButton,
  MudIcon,
  MudModal,
  MudInfoBox,
  MudSelect,
  MudTable,
  MudTag,
} from '@egov-moldova/mud-react';
import { apiClient, apiErrorKey } from '../../app/api-client';
import {
  type ActiveFilter,
  EmptyRegisterMessage,
  FilterBar,
  formatDateTime,
  MetadataLine,
  onModalDismiss,
  onRowOpen,
  PageHeader,
  RouterLink,
  RowActionsMenu,
  useToast,
} from '@simsm/ui-shared';
import { QueryState } from '../../components/QueryState/QueryState';
import { AlertsSectionNav } from '../../components/AlertsSectionNav';
import { AlertDefinitionsMenu } from '../../components/AlertDefinitionsMenu';
import { useConfirm } from '../../components/ConfirmDialog/ConfirmDialog';
import { TableFooter, usePageSize } from '../../components/TableFooter/TableFooter';

type EvaluatedState = 'firing' | 'normal' | 'inactive';

interface RuleExpression {
  kind: string;
  params: Record<string, unknown>;
}

export interface RuleDto {
  id: string;
  name: string;
  displayName: string;
  description: string | null;
  severity: 'critical' | 'warning' | 'info';
  labels: Record<string, string>;
  expression: RuleExpression;
  evalIntervalSeconds: number;
  forDurationSeconds: number;
  cooldownSeconds: number;
  notifyOnResolve: boolean;
  templateId: string | null;
  policyId: string | null;
  active: boolean;
  lastEvaluatedAtIso: string | null;
  evaluatedState: EvaluatedState;
  firingEventCount: number;
  nextEvaluationAtIso: string | null;
  lastFiredAtIso: string | null;
}

// Search folds diacritics on both sides, so a rule named with Romanian
// comma-below letters is reachable from an ASCII keyboard, and the two
// Unicode spellings of s-comma and t-comma (comma-below vs the legacy
// cedilla) match each other. NFD splits every accent into a combining mark
// and the range below removes them. Same shape as GeographyPage's own
// helper; deliberately file-local, like every other copy in this tree.
function comparable(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

const FIRED_FILTERS = ['today', '7d', '30d', 'none'] as const;

export function severitySemantic(
  s: 'critical' | 'warning' | 'info',
): 'danger' | 'brand' | 'neutral' {
  if (s === 'critical') return 'danger';
  if (s === 'warning') return 'brand';
  return 'neutral';
}

function stateSemantic(state: EvaluatedState): 'danger' | 'success' | 'muted' {
  if (state === 'firing') return 'danger';
  if (state === 'normal') return 'success';
  return 'muted';
}

// "La 6 ore" reads better than "21600s", and the administrator configures the
// interval in minutes, so minutes and hours are the only two units used. The
// unit is spelt out and counted in the reader's language, as the wireframe's
// "La 1 oră", "La 24 de ore" are.
export function intervalParts(seconds: number): { unit: 'hours' | 'minutes'; count: number } {
  const minutes = Math.max(1, Math.round(seconds / 60));
  return minutes % 60 === 0
    ? { unit: 'hours', count: minutes / 60 }
    : { unit: 'minutes', count: minutes };
}

type Translate = ReturnType<typeof useTranslate>['t'];

// The last evaluation the register shows beside the next one (UC-ALRT-01: name,
// severity, state, last evaluation). A rule Grafana has not reported on yet says so
// rather than leaving the line out (BUG-1-048).
export function lastEvaluationValue(lastEvaluatedAtIso: string | null, t: Translate): string {
  return lastEvaluatedAtIso
    ? formatDateTime(lastEvaluatedAtIso)
    : t('alerts.overview.never_evaluated');
}

export function formatInterval(seconds: number, t: Translate): string {
  const { unit, count } = intervalParts(seconds);
  return t(`alerts.interval.${unit}`, { count });
}

function daysAgoIso(days: number): string {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - days);
  return date.toISOString();
}

export function matchesFiredFilter(lastFiredAtIso: string | null, filter: string): boolean {
  if (!filter) return true;
  if (filter === 'none') return !lastFiredAtIso;
  if (!lastFiredAtIso) return false;
  if (filter === 'today') return lastFiredAtIso >= daysAgoIso(0);
  if (filter === '7d') return lastFiredAtIso >= daysAgoIso(6);
  if (filter === '30d') return lastFiredAtIso >= daysAgoIso(29);
  return true;
}

export function AlertRulesPage(): JSX.Element {
  const { t } = useTranslate();
  const [pageSize, setPageSize] = usePageSize('alert-rules');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [deleteTarget, setDeleteTarget] = useState<RuleDto | null>(null);
  // Article IV — a refusal the administrator never sees is indistinguishable
  // from one that never happened. Neither write on this page had an `onError`:
  // the delete closed its confirmation modal unconditionally, so a refused
  // delete looked exactly like an accepted one until the list refetched and the
  // rule came back, and a refused enable/disable simply snapped the switch back.
  const [actionErrorKey, setActionErrorKey] = useState<string | null>(null);

  const search = params.get('q') ?? '';
  const stateFilter = params.get('state') ?? '';
  const firedFilter = params.get('fired') ?? '';
  const page = Math.max(1, Number(params.get('page')) || 1);

  function setFilter(key: string, value: string): void {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'page') next.delete('page');
    setParams(next, { replace: true });
  }

  const rules = useQuery({
    queryKey: ['alert-rules'],
    queryFn: () =>
      apiClient.get<{ rules: RuleDto[] }>('/admin/alert-rules').then((r) => r.data.rules),
  });

  const update = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<RuleDto> }) =>
      apiClient.patch<RuleDto>(`/admin/alert-rules/${id}`, payload).then((r) => r.data),
    onSuccess: (saved) => {
      setActionErrorKey(null);
      // The row flips a state the user cannot otherwise see changed; the toast is
      // what tells them it took (wireframe AdminAlerte.tsx:1211).
      toast({
        variant: saved.active ? 'success' : 'info',
        title: saved.active ? t('alerts.toast.rule.activated') : t('alerts.toast.rule.deactivated'),
        text: saved.displayName,
        closeLabel: t('common.close'),
      });
      void queryClient.invalidateQueries({ queryKey: ['alert-rules'] });
    },
    onError: (err: unknown) =>
      setActionErrorKey(apiErrorKey(err, 'alerts.rules.error.toggle_failed')),
  });

  const remove = useMutation({
    mutationFn: ({ id }: { id: string; name: string }) =>
      apiClient.delete(`/admin/alert-rules/${id}`),
    onSuccess: (_data, variables) => {
      setActionErrorKey(null);
      toast({
        variant: 'info',
        title: t('alerts.toast.rule.deleted'),
        text: variables.name,
        closeLabel: t('common.close'),
      });
      void queryClient.invalidateQueries({ queryKey: ['alert-rules'] });
    },
    // The fallback names the operation: a delete that fails must never say that
    // saving failed.
    onError: (err: unknown) =>
      setActionErrorKey(apiErrorKey(err, 'alerts.rules.error.delete_failed')),
  });

  const all = useMemo(() => rules.data ?? [], [rules.data]);

  // Deactivating a rule stops it being evaluated and stops every notification it
  // would have sent — an outcome the row alone cannot show — so the wireframe
  // asks first (AdminAlerte.tsx:1584,:1769-1797) and only for the deactivating
  // direction. Activation is additive and goes straight through.
  async function toggleActive(rule: RuleDto): Promise<void> {
    if (
      rule.active &&
      !(await confirm({
        title: t('alerts.confirm.deactivate.title'),
        body: t('alerts.confirm.deactivate.body', { name: rule.displayName }),
        confirmLabel: t('alerts.action.deactivate'),
        cancelLabel: t('alerts.confirm.action.cancel'),
        destructive: true,
      }))
    ) {
      return;
    }
    setActionErrorKey(null);
    update.mutate({ id: rule.id, payload: { active: !rule.active } });
  }

  const filtered = useMemo(() => {
    const needle = comparable(search.trim());
    return all
      .filter(
        (r) =>
          !needle ||
          comparable(
            `${r.displayName} ${r.name} ${r.expression.kind} ${Object.entries(r.labels)
              .map(([k, v]) => `${k}=${v}`)
              .join(' ')}`,
          ).includes(needle),
      )
      .filter((r) => !stateFilter || r.evaluatedState === stateFilter)
      .filter((r) => matchesFiredFilter(r.lastFiredAtIso, firedFilter))
      .sort(
        (a, b) =>
          (b.lastFiredAtIso ?? '').localeCompare(a.lastFiredAtIso ?? '') ||
          a.displayName.localeCompare(b.displayName, 'ro'),
      );
  }, [all, search, stateFilter, firedFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const rows = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const firingCount = all.filter((r) => r.evaluatedState === 'firing').length;

  const stateOptions = [
    { value: '', label: t('alerts.filter.all') },
    { value: 'firing', label: t('alerts.state.firing') },
    { value: 'normal', label: t('alerts.state.normal') },
    { value: 'inactive', label: t('alerts.state.inactive') },
  ];
  const firedOptions = [
    { value: '', label: t('alerts.filter.all') },
    ...FIRED_FILTERS.map((value) => ({ value, label: t(`alerts.filter.fired.${value}`) })),
  ];

  // The search term gets no chip: it clears from the box's own ×, which is the
  // wireframe's own rule for the shared bar (BaraFiltre.tsx:35).
  const activeFilters: ActiveFilter[] = [
    ...(stateFilter
      ? [
          {
            key: 'state',
            label: t('alerts.column.state'),
            value: t(`alerts.state.${stateFilter}`),
            onRemove: () => setFilter('state', ''),
          },
        ]
      : []),
    ...(firedFilter
      ? [
          {
            key: 'fired',
            label: t('alerts.column.last_fired'),
            value: t(`alerts.filter.fired.${firedFilter}`),
            onRemove: () => setFilter('fired', ''),
          },
        ]
      : []),
  ];

  // The wireframe's `tbl zebra compact … leading-actions-table` (AdminAlerte.tsx
  // :3496-3504) as AGE's own table: six columns, the published mud-table's
  // limit. The leading column is the shared row-actions track (docs/ui-shape.md
  // §Shared leading row-actions column): a 44px content track plus mud-table's
  // 24px cell padding on each side, no visible heading, the accessible name
  // slotted in. Every cell is slotted, so mud-table's nowrap `.cell-text` never
  // ellipsises a name or a date.
  const columns: TableColumn[] = [
    { key: 'actions', label: '', width: '76px' },
    { key: 'alert', label: t('alerts.column.alert') },
    { key: 'severity', label: t('alerts.column.severity'), width: '124px' },
    { key: 'state', label: t('alerts.column.state'), width: '136px' },
    { key: 'evaluation', label: t('alerts.column.evaluation'), width: '22%' },
    { key: 'lastFired', label: t('alerts.column.last_fired'), width: '168px' },
  ];
  const tableRows: TableRowData[] = rows.map((r) => ({ id: r.id }));

  // An empty register keeps its table. The wireframe leaves the head, the
  // frame and the footer standing and puts the message in one centred cell
  // spanning every column (AdminAlerte.tsx:3556); a box in
  // place of the register hides what the register would even hold. Both
  // QueryState branches render this same node, because mud-table draws its
  // `empty` slot on its own once `rows` is [].
  const register = (
    <>
      <div className="simsm-alerts__register">
        <MudTable
          className="simsm-table"
          columns={columns}
          rows={tableRows}
          rowStyle="zebra"
          disableSort={true}
          aria-label={t('alerts.section.rules')}
          // The whole row opens the rule, the menu's first entry.
          hoverable
          onMudRowClick={onRowOpen((index) => {
            const r = rows[index];
            if (r) navigate(`/system-admin/alerts/${r.id}`);
          })}
        >
          <EmptyRegisterMessage>
            {all.length === 0 ? t('alerts.empty') : t('alerts.empty_filtered')}
          </EmptyRegisterMessage>
          <span slot="header-cell-actions" className="visually-hidden">
            {t('alerts.column.actions')}
          </span>
          {rows.map((r, index) => {
            const muted = r.evaluatedState === 'inactive' ? ' simsm-alerts__cell--muted' : '';
            return [
              <span
                key={`actions-${r.id}`}
                slot={`cell-actions-${index}`}
                className="simsm-alerts__cell simsm-alerts__cell--actions"
              >
                <RowActionsMenu
                  label={t('alerts.action.row_menu', { name: r.displayName })}
                  actions={[
                    {
                      label: t('alerts.action.open'),
                      onSelect: () => navigate(`/system-admin/alerts/${r.id}`),
                    },
                    {
                      label: t('alerts.action.edit'),
                      onSelect: () => navigate(`/system-admin/alerts/${r.id}/edit`),
                    },
                    {
                      label: r.active ? t('alerts.action.deactivate') : t('alerts.action.activate'),
                      separator: true,
                      onSelect: () => {
                        void toggleActive(r);
                      },
                    },
                    {
                      label: t('alerts.action.delete'),
                      danger: true,
                      onSelect: () => setDeleteTarget(r),
                    },
                  ]}
                />
              </span>,
              <span
                key={`alert-${r.id}`}
                slot={`cell-alert-${index}`}
                className={`simsm-alerts__cell-name${muted}`}
              >
                <RouterLink
                  to={`/system-admin/alerts/${r.id}`}
                  size="sm"
                  variant="strict"
                  underline="hover"
                >
                  {r.displayName}
                </RouterLink>
                <MetadataLine
                  wrap
                  items={[
                    {
                      label: t('alerts.column.type'),
                      value: t(`alerts.kind.${r.expression.kind}`),
                    },
                  ]}
                />
              </span>,
              <span
                key={`severity-${r.id}`}
                slot={`cell-severity-${index}`}
                className={`simsm-alerts__cell${muted}`}
              >
                <MudTag
                  variant="status"
                  size="sm"
                  semantic={severitySemantic(r.severity)}
                  label={t(`alerts.severity.${r.severity}`)}
                />
              </span>,
              <span
                key={`state-${r.id}`}
                slot={`cell-state-${index}`}
                className={`simsm-alerts__cell${muted}`}
              >
                <MudTag
                  variant="status"
                  type="outlined"
                  size="sm"
                  semantic={stateSemantic(r.evaluatedState)}
                  label={t(`alerts.state.${r.evaluatedState}`)}
                />
              </span>,
              <span
                key={`evaluation-${r.id}`}
                slot={`cell-evaluation-${index}`}
                className={`simsm-alerts__cell-name${muted}`}
              >
                <span>
                  {t('alerts.evaluation.every', {
                    interval: formatInterval(r.evalIntervalSeconds, t),
                  })}
                </span>
                <MetadataLine
                  wrap
                  items={[
                    {
                      label: t('alerts.evaluation.last'),
                      value: lastEvaluationValue(r.lastEvaluatedAtIso, t),
                    },
                    {
                      label: t('alerts.evaluation.next'),
                      value: r.nextEvaluationAtIso
                        ? formatDateTime(r.nextEvaluationAtIso)
                        : r.active
                          ? t('alerts.evaluation.on_activation')
                          : t('alerts.evaluation.unscheduled'),
                    },
                  ]}
                />
              </span>,
              <span
                key={`fired-${r.id}`}
                slot={`cell-lastFired-${index}`}
                className={`simsm-alerts__cell${muted}`}
              >
                {r.lastFiredAtIso ? (
                  formatDateTime(r.lastFiredAtIso)
                ) : (
                  <span className="simsm-alerts__muted-text">{t('alerts.never_fired')}</span>
                )}
              </span>,
            ];
          })}
        </MudTable>
      </div>
      <TableFooter
        total={filtered.length}
        page={currentPage}
        pageSize={pageSize}
        onPage={(next) => setFilter('page', next > 1 ? String(next) : '')}
        onPageSize={setPageSize}
      />
    </>
  );

  return (
    <section className="simsm-page simsm-alerts">
      <PageHeader
        title={t('alerts.section_title')}
        lede={t('alerts.section_lede')}
        actions={
          <>
            <AlertDefinitionsMenu />
            <MudButton
              shape="circular"
              variant="primary"
              size="md"
              onClick={() => navigate('/system-admin/alerts/new')}
            >
              <MudIcon slot="icon-start" name="plus-large" size={16} />
              {t('alerts.action.new')}
            </MudButton>
          </>
        }
      />

      <AlertsSectionNav firingCount={firingCount} />

      {actionErrorKey ? (
        <MudInfoBox
          emphasis="strong"
          role="alert"
          variant="error"
          closable
          closeLabel={t('common.close')}
          onMudClose={() => setActionErrorKey(null)}
        >
          {t(actionErrorKey)}
        </MudInfoBox>
      ) : null}

      <FilterBar
        search={{
          value: search,
          onChange: (value) => setFilter('q', value),
          placeholder: t('alerts.search.placeholder'),
          ariaLabel: t('alerts.search.aria'),
          clearLabel: t('common.filter.clear_search'),
        }}
        active={activeFilters}
        onReset={() => setParams(new URLSearchParams(), { replace: true })}
        labels={{
          filters: t('common.filter.button'),
          reset: t('common.filter.reset'),
          panelAriaLabel: t('alerts.filter.aria'),
          activeAriaLabel: t('common.filter.active_aria_label'),
        }}
        panel={
          <>
            <label className="simsm-filter-bar__field">
              <span className="simsm-filter-bar__field-label">{t('alerts.column.state')}</span>
              <MudSelect
                size="medium"
                value={stateFilter}
                aria-label={t('alerts.column.state')}
                onMudChange={(e: CustomEvent<{ value: string }>) =>
                  setFilter('state', e.detail.value)
                }
              >
                {stateOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </MudSelect>
            </label>
            <label className="simsm-filter-bar__field">
              <span className="simsm-filter-bar__field-label">{t('alerts.column.last_fired')}</span>
              <MudSelect
                size="medium"
                value={firedFilter}
                aria-label={t('alerts.column.last_fired')}
                onMudChange={(e: CustomEvent<{ value: string }>) =>
                  setFilter('fired', e.detail.value)
                }
              >
                {firedOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </MudSelect>
            </label>
          </>
        }
      />

      <QueryState query={rules} isEmpty={filtered.length === 0} empty={register}>
        {register}
      </QueryState>

      <MudModal
        closeLabel={t('common.close')}
        open={deleteTarget !== null}
        size="sm"
        titleText={t('alerts.confirm.delete.title')}
        // AGE-BUG(mudClose-bubbles) · vendor/age-design/SOURCE_COMMIT 7322a157 — applied to
        // every modal uniformly; a no-op here until a descendant emits its own mudClose.
        onMudClose={onModalDismiss(() => setDeleteTarget(null))}
      >
        {deleteTarget ? (
          <p className="simsm-alerts__confirm-body">
            {t('alerts.confirm.delete.body', { name: deleteTarget.displayName })}
          </p>
        ) : null}
        <div slot="actions" className="simsm-alerts__modal-actions">
          <MudButton
            shape="circular"
            variant="neutral"
            size="md"
            onClick={() => setDeleteTarget(null)}
          >
            {t('alerts.confirm.action.cancel')}
          </MudButton>
          <MudButton
            shape="circular"
            variant="destructive"
            size="md"
            onClick={() => {
              if (deleteTarget) {
                setActionErrorKey(null);
                remove.mutate({ id: deleteTarget.id, name: deleteTarget.displayName });
              }
              setDeleteTarget(null);
            }}
          >
            {t('alerts.confirm.action.confirm')}
          </MudButton>
        </div>
      </MudModal>
    </section>
  );
}
